/**
 * Pure helper that folds several declaring entity types' filterable property
 * schemas into one `{ property_type_name: PropertyTypeSchema }` map for a
 * kind-based association. This is the single shared source of truth consumed
 * by both SchemaFilterBuilder hooks (the combobox item list and the
 * operator-selection map), so they can never diverge.
 *
 * Two deliberate behaviors, called out so future readers don't "fix" them:
 *
 * 1. Field-type disagreement drops the property for EVERY declaring type,
 *    including the first/system type where the backend's OR-of-EXISTS would
 *    still accept it. This mirrors the issue's safety rule: identical
 *    `field_type_name` across all declaring types is the only case guaranteed
 *    to pass the backend's AND-of-operators validation, so a property whose
 *    field type disagrees between declaring types is omitted entirely rather
 *    than risk a 400 on some combination of targets.
 * 2. "First declaring type wins" for the surviving schema and label relies on
 *    `resolveAssociationTargetTypes` preserving the object-types order, which
 *    is system-first (built-ins before custom). If that upstream ordering
 *    changes, which schema/label survives changes with it.
 */
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { logger } from '@/lib/logger';

export function mergeAssociatedTargetProperties(
  declaringTypes: string[],
  targetFilterableProps: Record<string, PropertyTypeSchema[]>
): Record<string, PropertyTypeSchema> {
  // A Map (rather than a plain object) lets a poisoned entry be removed via
  // `.delete()` instead of a dynamic `delete obj[key]`, which is banned by
  // `@typescript-eslint/no-dynamic-delete`.
  const merged = new Map<string, PropertyTypeSchema>();
  // Names dropped for field-type disagreement — never re-added, even if a
  // later declaring type would otherwise agree with an earlier one.
  const poisoned = new Set<string>();

  for (const type of declaringTypes) {
    for (const prop of targetFilterableProps[type] ?? []) {
      const name = prop.property_type_name;
      if (poisoned.has(name)) {
        continue;
      }
      const existing = merged.get(name);
      if (!existing) {
        // Shallow clone so the cached schema object is never mutated below.
        merged.set(name, { ...prop });
        continue;
      }
      if (existing.field_type_name !== prop.field_type_name) {
        merged.delete(name);
        poisoned.add(name);
        logger.debug(
          '[SchemaFilterBuilder] Dropping associated property with conflicting field types',
          {
            property: name,
            fieldTypes: [existing.field_type_name, prop.field_type_name],
          }
        );
        continue;
      }
      // Same field type: union `options` deduped by `value`, first-seen label
      // wins. All other schema fields (including `label`) stay from the
      // first declaring type.
      const existingOptions = existing.options ?? [];
      const seenValues = new Set(existingOptions.map(option => option.value));
      const newOptions = (prop.options ?? []).filter(
        option => !seenValues.has(option.value)
      );
      if (newOptions.length > 0) {
        merged.set(name, {
          ...existing,
          options: [...existingOptions, ...newOptions],
        });
      }
    }
  }

  return Object.fromEntries(merged);
}
